import { EXTERNAL_ICON_URLS } from './canvasData'

const HERO_RX = 300
const HERO_RY = 160
const HERO_RX_CHAT = 420
const HERO_RY_CHAT = 310
const DECO_COUNT = 32

const QUADRANTS = [
  { start: -Math.PI / 2, end: 0           }, // top-right
  { start: 0,            end: Math.PI / 2  }, // bottom-right
  { start: Math.PI / 2,  end: Math.PI      }, // bottom-left
  { start: -Math.PI,     end: -Math.PI / 2 }, // top-left
]
const PER_QUADRANT = DECO_COUNT / QUADRANTS.length

export function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3)
}

function pushOutOfHero(targetX, targetY, cx, cy, canvasMargin, W, H, heroRx = HERO_RX, heroRy = HERO_RY) {
  const dx = targetX - cx
  const dy = targetY - cy
  const rx = heroRx + canvasMargin
  const ry = heroRy + canvasMargin
  const ev = (dx / rx) ** 2 + (dy / ry) ** 2
  if (ev < 1) {
    const push = 1.05 / Math.sqrt(ev)
    return {
      x: Math.max(canvasMargin, Math.min(W - canvasMargin, cx + dx * push)),
      y: Math.max(canvasMargin, Math.min(H - canvasMargin, cy + dy * push)),
    }
  }
  return { x: targetX, y: targetY }
}

export function makeTriangle(cx, cy, W, H, item, index, total) {
  const size = Math.random() * 28 + 42
  const canvasMargin = Math.ceil(size * 1.15)

  const angle = (index / total) * Math.PI * 2 + (Math.random() - 0.5) * 0.55
  const cosA = Math.cos(angle), sinA = Math.sin(angle)
  const maxR = Math.min(
    (W / 2 - canvasMargin) / Math.max(Math.abs(cosA), 0.01),
    (H / 2 - canvasMargin) / Math.max(Math.abs(sinA), 0.01)
  )
  const r = maxR * (0.64 + Math.random() * 0.14)
  let targetX = Math.max(canvasMargin, Math.min(W - canvasMargin, cx + cosA * r))
  let targetY = Math.max(canvasMargin, Math.min(H - canvasMargin, cy + sinA * r))
  const pushed = pushOutOfHero(targetX, targetY, cx, cy, canvasMargin, W, H)
  targetX = pushed.x
  targetY = pushed.y

  return {
    startX: cx, startY: cy,
    x: cx, y: cy,
    targetX, targetY,
    progress: 0,
    currentAlpha: 1,
    category: item.category,
    label: item.label,
    size,
    canvasMargin,
    exclusionRadius: size + 28,
    rotation: Math.random() * Math.PI * 2,
    rotSpeed: (Math.random() - 0.5) * (EXTERNAL_ICON_URLS[item.label] ? 0.018 : 0.06),
    idleAmp: Math.random() * 5 + 4,
    idleFreqX: Math.random() * 0.28 + 0.18,
    idleFreqY: Math.random() * 0.22 + 0.14,
    idlePhaseX: Math.random() * Math.PI * 2,
    idlePhaseY: Math.random() * Math.PI * 2,
    r: item.r, g: item.g, b: item.b,
  }
}

function makeDecorativeTriangle(cx, cy, W, H, angle) {
  const size = Math.random() * 10 + 15
  const canvasMargin = Math.ceil(size * 1.1)

  const cosA = Math.cos(angle), sinA = Math.sin(angle)
  const maxR = Math.min(
    (W / 2 - canvasMargin) / Math.max(Math.abs(cosA), 0.01),
    (H / 2 - canvasMargin) / Math.max(Math.abs(sinA), 0.01)
  )
  const r = maxR * (0.55 + Math.random() * 0.42)
  let targetX = Math.max(canvasMargin, Math.min(W - canvasMargin, cx + cosA * r))
  let targetY = Math.max(canvasMargin, Math.min(H - canvasMargin, cy + sinA * r))
  const pushed = pushOutOfHero(targetX, targetY, cx, cy, canvasMargin, W, H)
  targetX = pushed.x
  targetY = pushed.y

  const direction = Math.random() > 0.5 ? 1 : -1
  return {
    startX: cx, startY: cy,
    x: cx, y: cy,
    targetX, targetY,
    progress: 0,
    currentAlpha: 0.3,
    label: null,
    size,
    canvasMargin,
    exclusionRadius: size + 8,
    rotation: Math.random() * Math.PI * 2,
    rotSpeed: direction * (0.012 + Math.random() * 0.025),
    idleAmp:   Math.random() * 4 + 2,
    idleFreqX: Math.random() * 0.3 + 0.15,
    idleFreqY: Math.random() * 0.25 + 0.12,
    idlePhaseX: Math.random() * Math.PI * 2,
    idlePhaseY: Math.random() * Math.PI * 2,
    r: 0, g: 0, b: 0,
  }
}

export function makeDecorativeTriangles(cx, cy, W, H) {
  return QUADRANTS.flatMap(({ start, end }) =>
    Array.from({ length: PER_QUADRANT }, (_, i) => {
      const t = (i + 0.5) / PER_QUADRANT
      const angle = start + t * (end - start) + (Math.random() - 0.5) * 0.25
      return makeDecorativeTriangle(cx, cy, W, H, angle)
    })
  )
}

export function resolveCollisions(triangles, W, H, cx, cy, iterations = 30, heroRx = HERO_RX, heroRy = HERO_RY) {
  for (let iter = 0; iter < iterations; iter++) {
    for (let i = 0; i < triangles.length; i++) {
      for (let j = i + 1; j < triangles.length; j++) {
        const a = triangles[i], b = triangles[j]
        const minDist = a.exclusionRadius + b.exclusionRadius
        const dx = b.targetX - a.targetX
        const dy = b.targetY - a.targetY
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.001
        if (dist < minDist) {
          const overlap = (minDist - dist) / 2
          const nx = dx / dist, ny = dy / dist
          a.targetX = Math.max(a.canvasMargin, Math.min(W - a.canvasMargin, a.targetX - nx * overlap))
          a.targetY = Math.max(a.canvasMargin, Math.min(H - a.canvasMargin, a.targetY - ny * overlap))
          b.targetX = Math.max(b.canvasMargin, Math.min(W - b.canvasMargin, b.targetX + nx * overlap))
          b.targetY = Math.max(b.canvasMargin, Math.min(H - b.canvasMargin, b.targetY + ny * overlap))
        }
      }
    }

    for (const t of triangles) {
      const pushed = pushOutOfHero(t.targetX, t.targetY, cx, cy, t.canvasMargin, W, H, heroRx, heroRy)
      t.targetX = pushed.x
      t.targetY = pushed.y
    }
  }
}

export function repositionForChat(triangles, W, H, cx, cy) {
  for (const t of triangles) {
    t.startX = t.x
    t.startY = t.y
    t.progress = 0
    const pushed = pushOutOfHero(t.targetX, t.targetY, cx, cy, t.canvasMargin, W, H, HERO_RX_CHAT, HERO_RY_CHAT)
    t.targetX = pushed.x
    t.targetY = pushed.y
  }
  resolveCollisions(triangles, W, H, cx, cy, 20, HERO_RX_CHAT, HERO_RY_CHAT)
}
